<template>
  <div class="downloader-wrapper">
    <div class="button-downloader">
      <BaseButton
        block
        elevation="0"
        v-bind="computedAttributes"
        :class="currentActiveControl.customClass"
        class="text-none"
        :disabled="shouldDisableDownloader"
        @click="downloaderClickHandler()"
      >
        <BaseSpriteIcon
          :custom-class="currentActiveControl.icon"
          :icon-name="currentActiveControl.icon"
        />
        {{ currentActiveControl.shortLabel }}
      </BaseButton>
      <div
        v-if="currentActiveControl.showProgress"
        class="downloader-progress-wrapper"
      >
        <div
          class="downloader-progress-bar"
          :style="{ width: progress + '%' }"
        />
      </div>
    </div>
    <DownloadInformer
      v-if="showInformer"
      :is-content-downloaded="isContentDownloaded"
      :is-audio-downloaded="isAudioDownloaded"
      :has-audio="hasAudio"
      :expanded="true"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FontLoader from '@/services/utils/FontLoader';
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('Downloader.vue');
import CustomErrorEnum from '@/enums/CustomErrorEnum';
import TrackingItemChangeTypes from '@/enums/TrackingItemChangeTypes';
import DownloaderEventEnum from '@/enums/DownloaderEventEnum';

import BaseButton from '@/components/base/BaseButton/BaseButton';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon';
import DownloadInformer from '@/components/base/DownloadInformer/DownloadInformer';
import AssetsManager from '@/services/AssetsManager/AssetsManager';
import FsErrorsEnum from '@/enums/FsErrorsEnum';

import Utils from '@/services/utils/Utils';
import PopupNamesEnum from '@/enums/PopupNamesEnum';
import LoginStatesEnum from '@/enums/LoginStatesEnum';

const DownloaderEnum = {
  CANCEL: 'Cancel',
  PAUSED: 'DownloadingPaused',
  UPDATE: 'Update',
  DELETE: 'Delete',
  DELETE_AUDIO: 'DeleteAudio',
  DOWNLOAD: 'Download',
  DOWNLOAD_AUDIO: 'DownloadAudio',
  ON_SD_CARD: 'OnSDCard'
};

class DownloaderControl {
  constructor(buildData) {
    this.id = buildData.id;
    this.label = buildData.label;
    this.icon = buildData.icon;
    this.customClass = [
      buildData.customClass || '',
      buildData.disabledClass || ''
    ]
      .join(' ')
      .trim();
    this.shortLabel = buildData.shortLabel;
    this.handler = buildData.handler;
    this.displayHandler = buildData.displayHandler;
    this.showProgress = buildData.showProgress || false;
    this.attrs = buildData.attrs || {};
  }
}

class DownloaderControlBuilder {
  setId(id) {
    this.id = id;
    return this;
  }

  setIcon(icon) {
    this.icon = icon;
    return this;
  }

  setCustomClass(customClass) {
    this.customClass = customClass;
    return this;
  }

  setDisabledClass(disabledClass) {
    this.disabledClass = disabledClass;
    return this;
  }

  setLabel(label) {
    this.label = label || '';
    return this;
  }

  setShortLabel(shortLabel) {
    this.shortLabel = shortLabel || '';
    return this;
  }

  setHandler(handler) {
    this.handler = handler;
    return this;
  }

  setDisplayHandler(displayHandler) {
    this.displayHandler = displayHandler;
    return this;
  }

  setShowProgress(showProgress) {
    this.showProgress = showProgress;
    return this;
  }

  setAttrs(attrs) {
    this.attrs = attrs;
    return this;
  }

  build() {
    return new DownloaderControl(this);
  }
}

function $_createDownloaderControl(type) {
  let downloaderControlBuilder = new DownloaderControlBuilder();

  downloaderControlBuilder.setId(type);
  switch (type) {
    case DownloaderEnum.CANCEL:
      downloaderControlBuilder
        .setIcon('ico-download')
        .setLabel(this.$t('Downloader.audioDownloadCancel.label'))
        .setShortLabel(this.$t('Downloader.audioDownloadCancelShort.label'))
        .setHandler(this.$_cancelDownload)
        .setDisplayHandler(this.$_cancelDisplayHandler)
        .setShowProgress(true)
        .setAttrs({ color: this.color });
      break;
    case DownloaderEnum.PAUSED:
      downloaderControlBuilder
        .setIcon('ico-stop')
        .setLabel(this.$t('Downloader.audioDownloadingPaused.label'))
        .setShortLabel(this.$t('Downloader.audioDownloadingPausedShort.label'))
        .setHandler(this.$_pauseDownload)
        .setDisplayHandler(this.$_pauseDisplayHandler)
        .setAttrs({ color: this.color });
      break;
    case DownloaderEnum.UPDATE:
      downloaderControlBuilder
        .setIcon('ico-download')
        .setLabel(this.$t('Downloader.update.label'))
        .setShortLabel(this.$t('Downloader.update.label'))
        .setHandler(this.$_updateAssets)
        .setDisplayHandler(this.$_updateDisplayHandler)
        .setAttrs({ color: this.color });
      break;
    case DownloaderEnum.DELETE_AUDIO:
      downloaderControlBuilder
        .setIcon('ico-export')
        .setCustomClass('theme--light')
        .setLabel(this.$t('Downloader.deleteAudioAsset.label'))
        .setShortLabel(this.$t('Downloader.deleteAudioAssetShort.label'))
        .setHandler(this.$_deleteAssetsAudio)
        .setDisplayHandler(this.$_deleteAudioDisplayHandler)
        .setAttrs({ color: this.color });
      break;
    case DownloaderEnum.DELETE:
      downloaderControlBuilder
        .setIcon('ico-delete-solid')
        .setCustomClass('theme--light')
        .setLabel(this.$t('Downloader.audioDelete.label'))
        .setShortLabel(this.$t('Downloader.audioDeleteShort.label'))
        .setHandler(this.$_deletePublication)
        .setDisplayHandler(this.$_deleteDisplayHandler)
        .setAttrs({ color: this.color });
      break;
    case DownloaderEnum.DOWNLOAD:
      downloaderControlBuilder
        .setIcon('ico-download')
        .setLabel(this.$t('Downloader.audioDownload.label'))
        .setShortLabel(this.$t('Downloader.audioDownloadShort.label'))
        .setHandler(this.$_download)
        .setDisplayHandler(this.$_downloadDisplayHandler)
        .setAttrs({ color: this.color });
      break;
    case DownloaderEnum.DOWNLOAD_AUDIO:
      downloaderControlBuilder
        .setIcon('ico-download')
        .setLabel(this.$t('Downloader.downloadAudio.label'))
        .setShortLabel(this.$t('Downloader.audioDownloadShort.label'))
        .setHandler(this.$_download)
        .setDisplayHandler(this.$_downloadAudioDisplayHandler)
        .setAttrs({ color: this.color });
      break;
    case DownloaderEnum.ON_SD_CARD:
      downloaderControlBuilder
        .setIcon('svg-ico-download-audio-sdcard')
        .setCustomClass('primary')
        .setLabel(this.$t('Downloader.audioOnSDCard.label'))
        .setShortLabel(this.$t('Downloader.audioOnSDCardShort.label'))
        .setHandler(this.$_onSdCard)
        .setDisplayHandler(this.$_onSdCardDisplayHandler)
        .setAttrs({ color: this.color });
      break;
  }
  return downloaderControlBuilder.build();
}

function $_createDownloaderControls() {
  const _createDownloaderControl = this.$_createDownloaderControl;
  const downloaderControls = [];
  const controlOrder = [
    DownloaderEnum.DOWNLOAD,
    DownloaderEnum.DOWNLOAD_AUDIO,
    DownloaderEnum.DELETE_AUDIO,
    DownloaderEnum.DELETE,
    DownloaderEnum.UPDATE,
    DownloaderEnum.PAUSED,
    DownloaderEnum.CANCEL,
    DownloaderEnum.ON_SD_CARD
  ];
  controlOrder.forEach(function(type) {
    downloaderControls.push(_createDownloaderControl(type));
  });
  return downloaderControls;
}

async function changeNotifier(nofityInfo, changeType) {
  switch (changeType) {
    case TrackingItemChangeTypes.DOWNLOAD:
      this.$_processDownload(nofityInfo);
      break;
    case TrackingItemChangeTypes.ASSET_DOWNLOADED: {
      await this.$_processAssetChanged(nofityInfo);
      this.currentActiveControl = this.$_getCurrentControl();
      break;
    }
    case TrackingItemChangeTypes.ASSET_CANCELED:
    case TrackingItemChangeTypes.ASSETS_REMOVED:
      await this.$_processAssetChanged(nofityInfo);
      this.$_processCancel();
      break;
    case TrackingItemChangeTypes.CANCEL:
      this.$_processCancel();
      break;
    case TrackingItemChangeTypes.REMOVE:
      this.$_processRemove();
      break;
    case TrackingItemChangeTypes.ERROR:
      this.$_onDownloadError(nofityInfo);
      break;
  }
}

function $_dropProgress() {
  this.downloaded = 0;
  this.progress = 0;
}

export default {
  name: 'Downloader',
  components: {
    BaseButton,
    BaseSpriteIcon,
    DownloadInformer
  },
  props: {
    bookId: {
      type: String,
      required: true
    },
    hasAudio: {
      type: Boolean,
      required: false,
      default: false
    },
    shortLabels: {
      type: Boolean,
      required: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    postActionName: {
      type: String,
      required: false,
      default: ''
    },
    showInformer: {
      type: Boolean,
      required: false,
      default: false
    },
    color: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    const assetsInQueue = AssetsManager.getAssetQueueById(this.bookId);
    return {
      downloaded: 0,
      progress: 0,
      changeNotifierId: 'changeNotifierId' + Utils.uuid(),
      downloaderControls: [],
      currentActiveControl: {},
      assetsInQueue,
      trackingItemView: null,
      endSubscription: null,
      inited: false
    };
  },
  computed: {
    ...mapGetters('ContextStore', { systemLanguage: 'getSystemLanguage' }),
    narrow() {
      return this.$store.getters['MediaDetectorStore/mediaSize'].narrow;
    },
    isOnline() {
      return this.$store.getters['ContextStore/isOnline'];
    },
    isAndroid() {
      return this.$store.getters['ContextStore/isAndroid'];
    },
    isPopupOpened() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'];
    },
    displayShortLabels() {
      return this.shortLabels || this.narrow;
    },
    shouldDisableDownloader() {
      return (
        !(
          this.currentActiveControl.id === DownloaderEnum.DELETE ||
          this.currentActiveControl.id === DownloaderEnum.DELETE_AUDIO
        ) &&
        (this.disabled || !this.isOnline)
      );
    },
    isContentDownloaded() {
      return this.trackingItemView && this.trackingItemView.isContentDownloaded;
    },
    isAudioDownloaded() {
      return this.trackingItemView && this.trackingItemView.isAudioDownloaded;
    },
    limitedAccess() {
      return this.$store.getters['PublicationStore/isLimitedAccess'](
        this.bookId
      );
    },
    computedAttributes() {
      return {
        ...this.$attrs,
        ...this.currentActiveControl.attrs
      };
    },
    isGuestUser() {
      return this.$store.getters['UserStore/isGuestUser'];
    }
  },
  watch: {
    systemLanguage() {
      if (this.inited) {
        this.initControls();
      }
    },
    progress(newVal, oldVal) {
      this.$_changeStateByProgress(newVal, oldVal);
    },
    async isOnline(online) {
      if (online && !this.inited) {
        await this.initDownloader();
      }
    }
  },
  async mounted() {
    this.$_subscribe();
    await this.initDownloader();
  },
  beforeDestroy() {
    if (this.endSubscription) {
      this.endSubscription();
    }
    AssetsManager.removeDownloadListener(this.bookId, this.changeNotifierId);
  },
  errorCaptured(error) {
    this.$_errorHandler(error);
  },
  methods: {
    $_subscribe() {
      this.endSubscription = this.$store.subscribeAction({
        after: action => {
          const type = action.type;
          switch (type) {
            case 'PublicationStore/initBookAssets':
              if (action.payload.forced) {
                this.initDownloader();
              }
              break;
          }
        }
      });
    },
    async initDownloader() {
      try {
        this.trackingItemView = await AssetsManager.getPublicationTrackingItemView(
          this.bookId
        );
        const nofityInfo = {
          progress: this.trackingItemView.progress,
          totalFiles: this.trackingItemView.totalFiles
        };
        this.$_processDownload(nofityInfo);
        AssetsManager.addChangeListener(
          this.bookId,
          this.changeNotifier,
          this.changeNotifierId
        );
        this.initControls();
        this.$_emitEvent(DownloaderEventEnum.INIT, {
          trackingItemView: this.trackingItemView
        });
        this.inited = true;
      } catch (error) {
        this.$_errorHandler(error);
      }
    },
    initControls() {
      this.downloaderControls = this.$_createDownloaderControls();
      this.currentActiveControl = this.$_getCurrentControl();
    },
    downloaderClickHandler() {
      if (this.isGuestUser) {
        this.$_openConnectPopup();
        return;
      }

      this.currentActiveControl.handler();
    },
    $_openConnectPopup() {
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.CONNECT,
        popupContext: { initState: LoginStatesEnum.REGISTRATION }
      });
    },
    $_changeStateByProgress(progress, oldProgress = null) {
      if (progress === 0) {
        return;
      }
      if (progress === 100) {
        if (!this.trackingItemView.outdatedData) {
          const controlName = this.trackingItemView.hasAudio
            ? DownloaderEnum.DELETE_AUDIO
            : DownloaderEnum.DELETE;
          this.$_setCurrentControl(controlName);
        }
        if (oldProgress !== null) {
          this.$_onFinishDownload();
        }
        return;
      }
      if (!this.isOnline) {
        return;
      }
      if (oldProgress === 0) {
        this.$_onStartDownload();
      }
      if (
        this.trackingItemView.isContentDownloaded &&
        progress !== 0 &&
        oldProgress === 0
      ) {
        return;
      }

      this.$_continueDownload();
    },
    $_errorHandler(error) {
      logger.error(`get error for bookId:${this.bookId}, error: ${error}`);
      this.currentActiveControl = this.$_createDownloaderControl(
        DownloaderEnum.DOWNLOAD
      );
      this.currentActiveControl.customClass += ' disabled';
      this.$_emitEvent(DownloaderEventEnum.ERROR, {
        error: error,
        bookId: this.bookId
      });
      return false;
    },
    changeNotifier,
    $_dropProgress,
    $_createDownloaderControls,
    $_createDownloaderControl,
    async $_downloadPublication() {
      await this.$_downloadPublicationFonts();
      try {
        const assetNames = await AssetsManager.getAssetNameList(
          this.trackingItemView.hasAudio
        );
        const bookId = this.bookId;
        const assetsForDownloading = await this.$_getAssetsForDownloading(
          bookId,
          assetNames
        );
        this.assetsInQueue = assetsForDownloading;
        await this.downloadAllAssets(bookId, assetsForDownloading);
        this.$_updateLibraryAssetsSource();
      } catch (err) {
        if (err.code === FsErrorsEnum.EXCEED_STORAGE_QUOTA) {
          const text = this.$t('App.Filesystem.Size.error');
          this.$store.dispatch('ManagePopupStore/openErrorToaster', {
            text
          });
          await this.$_cancelDownload();
          return;
        } else if (
          err &&
          err.type === CustomErrorEnum.INVALID_ASSET_VERSION_FILE
        ) {
          logger.fatal(err);
          AssetsManager.cancelDownload(this.bookId);
          let text = 'Oops, something went wrong with downloading content';
          this.$store.dispatch('ManagePopupStore/openErrorToaster', {
            text
          });
          return;
        }
        this.$_onDownloadError(err);
      }
    },
    async $_downloadPublicationFonts() {
      try {
        const fontNames = FontLoader.getBookFontNames();
        await FontLoader.downloadFonts(fontNames);
      } catch (e) {
        logger.error(`font loading error: ${e}`);
      }
    },
    async $_getAssetsForDownloading(bookId, assetNames) {
      const assetsForDownloading = [];
      for (let assetName of assetNames) {
        const isDownloaded = await this.$_isDownloaded(bookId, assetName);
        if (!isDownloaded) {
          assetsForDownloading.push(assetName);
        }
      }
      return assetsForDownloading;
    },
    async downloadAllAssets(bookId, assetNames) {
      const promises = assetNames.map(assetName =>
        AssetsManager.downloadAsset(bookId, assetName)
      );
      return Promise.all(promises);
    },
    async $_isDownloaded(bookId, assetName) {
      const isDownloaded = await AssetsManager.isAssetDownloaded(
        bookId,
        assetName
      );
      return isDownloaded;
    },

    async $_updateLibraryAssetsSource() {
      if (!this.postActionName) {
        return;
      }
      this.$store.dispatch(this.postActionName, {
        bookId: this.bookId
      });
    },
    $_processDownload(nofityInfo) {
      const progress =
        Math.round((nofityInfo.progress / nofityInfo.totalFiles) * 10000) / 100;
      this.progress = Math.min(progress, 100);
    },
    async $_processAssetChanged() {
      const updatedTrackingItemView = await AssetsManager.getPublicationTrackingItemView(
        this.bookId
      );
      const updateProps = ['isContentDownloaded', 'isAudioDownloaded'];
      this.trackingItemView.progress = updatedTrackingItemView.progress;
      updateProps.forEach(prop => {
        const isAssetDownloaded =
          updatedTrackingItemView[prop] !== this.trackingItemView[prop];
        if (isAssetDownloaded) {
          this.trackingItemView[prop] = updatedTrackingItemView[prop];
          this.$_updateLibraryAssetsSource();
        }
      });
    },
    $_processCancel() {
      this.$_dropProgress();
      const currentControl =
        this.trackingItemView.hasAudio &&
        !this.isAudioDownloaded &&
        this.isContentDownloaded
          ? DownloaderEnum.DOWNLOAD_AUDIO
          : DownloaderEnum.DOWNLOAD;
      this.$_setCurrentControl(currentControl);
      this.$_updateLibraryAssetsSource();
    },
    async $_processRemove() {
      this.trackingItemView = await AssetsManager.getPublicationTrackingItemView(
        this.bookId
      );
      this.$_setCurrentControl(DownloaderEnum.DOWNLOAD);
      this.progress = 0;
      this.$_emitEvent(DownloaderEventEnum.DELETED, {
        trackingItemView: this.trackingItemView
      });
    },
    $_onStartDownload() {
      this.trackingItemView.isStartedDownload = true;
    },
    $_continueDownload() {
      this.$_setCurrentControl(DownloaderEnum.CANCEL);
    },
    async $_onFinishDownload() {
      this.trackingItemView = await AssetsManager.getPublicationTrackingItemView(
        this.bookId
      );
      this.$_updateLibraryAssetsSource();
      this.$_emitEvent(DownloaderEventEnum.DOWNLOADED, {
        trackingItemView: this.trackingItemView
      });
    },
    $_onDownloadError(error) {
      this.trackingItemView.isStartedDownload = false;
      if (error.type === CustomErrorEnum.CLEAR_QUEUE) {
        this.$_setCurrentControl(DownloaderEnum.DOWNLOAD);
        return;
      }
      this.$_setCurrentControl(DownloaderEnum.PAUSED);

      const err =
        error instanceof Error ? error : JSON.stringify(error || '', null, 2);
      logger.error(`Get error on download book id ${this.bookId} error ${err}`);
    },
    $_getCurrentControl() {
      const currentControl = this.downloaderControls.find(downloaderControl => {
        return downloaderControl.displayHandler();
      });
      return currentControl;
    },
    $_setCurrentControl(controlId) {
      this.currentActiveControl = this.downloaderControls.find(
        downloaderControl => {
          return downloaderControl.id === controlId;
        }
      );
    },
    $_cancelDownload() {
      this.trackingItemView.isStartedDownload = false;
      return this.$_cancelDownloadAssets(this.assetsInQueue.reverse())
        .then(() => {
          this.$_processCancel();
        })
        .catch(error => {
          logger.error(
            `Get error on cancel download book id ${this.bookId} error ${error}`
          );
          this.$_dropProgress();
          this.$_setCurrentControl(DownloaderEnum.PAUSED);
        });
    },
    async $_cancelDownloadAssets(assetNames) {
      const bookId = this.bookId;
      for (let assetName of assetNames) {
        await AssetsManager.cancelDownloadingAsset(bookId, assetName);
      }
    },
    $_cancelDisplayHandler() {
      return (
        this.trackingItemView.isStartedDownload &&
        this.trackingItemView.progress >= 0
      );
    },
    $_pauseDownload() {
      return this.$_downloadPublication();
    },
    $_pauseDisplayHandler() {
      return (
        (!this.trackingItemView.isContentDownloaded ||
          !this.trackingItemView.isAudioDownloaded) &&
        this.trackingItemView.totalFile > this.trackingItemView.progress
      );
    },
    $_updateAssets() {
      this.trackingItemView.isStartedDownload = true;
      this.$_setCurrentControl(DownloaderEnum.CANCEL);
      return AssetsManager.updatePublication(this.bookId)
        .then(() => {
          const controlName = this.trackingItemView.isAudioDownloaded
            ? DownloaderEnum.DELETE_AUDIO
            : DownloaderEnum.DELETE;
          this.$_setCurrentControl(controlName);
          this.trackingItemView.isStartedDownload = false;
          this.$_emitEvent(DownloaderEventEnum.UPDATED, {
            bookId: this.bookId
          });
        })
        .catch(error => {
          logger.error(
            `Get error on update download book id ${this.bookId} error ${error}`
          );
          this.trackingItemView.isStartedDownload = false;
          this.$_setCurrentControl(DownloaderEnum.PAUSED);
        });
    },
    $_updateDisplayHandler() {
      if (this.trackingItemView.hasAudio) {
        return (
          this.trackingItemView.isContentDownloaded &&
          this.trackingItemView.isAudioDownloaded &&
          this.trackingItemView.outdatedData
        );
      }
      return (
        this.trackingItemView.isContentDownloaded &&
        this.trackingItemView.outdatedData
      );
    },
    async $_deletePublication() {
      try {
        await AssetsManager.removePublication(this.bookId);
        this.$_updateLibraryAssetsSource();
      } catch (error) {
        logger.error(
          `Get error on delete book id ${this.bookId} error ${error}`
        );
      }
    },
    async $_deleteAssetsAudio() {
      try {
        const audioAssets = await AssetsManager.getAssetNameAudioList();
        await AssetsManager.removeAssets(this.bookId, audioAssets);
        this.$_updateLibraryAssetsSource();
      } catch (error) {
        logger.error(
          `Get error on delete book id ${this.bookId} error ${error}`
        );
      }
    },
    $_deleteAudioDisplayHandler() {
      if (!this.trackingItemView.hasAudio) {
        return false;
      }
      return (
        this.trackingItemView.isContentDownloaded &&
        this.trackingItemView.isAudioDownloaded &&
        !this.trackingItemView.outdatedData
      );
    },
    $_deleteDisplayHandler() {
      let canDelete = false;
      if (this.trackingItemView.hasAudio) {
        canDelete =
          this.trackingItemView.isContentDownloaded &&
          this.trackingItemView.isAudioDownloaded &&
          !this.trackingItemView.isAudioOnSDCard;
      } else {
        canDelete = this.trackingItemView.isContentDownloaded;
      }
      return canDelete && !this.trackingItemView.outdatedData;
    },
    async $_download() {
      if (this.limitedAccess) {
        if (this.isAndroid && this.isPopupOpened) {
          this.$store.dispatch('ManagePopupStore/closePopup', {
            name: PopupNamesEnum.EXTRAS_POPUP
          });
        }
        this.$store.dispatch('UserStore/openLimitedAccessPopup');
        return;
      }
      const fileTransportsReady = await AssetsManager.fileTransportAvailable();
      if (!fileTransportsReady) {
        this.$store.dispatch('ManagePopupStore/openUsageSuggestionPopup');
        return;
      }
      return this.$_downloadPublication();
    },
    $_downloadDisplayHandler() {
      return !this.trackingItemView.isContentDownloaded;
    },
    $_downloadAudioDisplayHandler() {
      return (
        this.trackingItemView.hasAudio &&
        this.trackingItemView.isContentDownloaded &&
        !this.trackingItemView.isAudioDownloaded &&
        this.trackingItemView.progress !== 0
      );
    },
    $_onSdCard() {
      logger.warn('_onSdCard');
    },
    $_onSdCardDisplayHandler() {
      return this.trackingItemView.isAudioOnSDCard;
    },
    $_emitEvent(eventName, payload) {
      this.$emit('downloaderEvent', {
        type: eventName,
        data: payload
      });
    }
  }
};
</script>

<style lang="less" src="./Downloader.less"></style>
